import { Numberu64, Socean } from "@soceanfi/stake-pool-sdk";
import type { Connection, Signer, Transaction, TransactionVersion } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { toLamports, WRAPPED_SOL_MINT } from "@stepfinance/common";
import type { Dispatch, SetStateAction } from "react";

import { confirmOptions } from "../../constants/transaction";
import type { MultiTxnNotificationStrings } from "../../contexts/language/languageTypes";
import type { ConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { JupiterSwapHandler } from "../../utils/jupiter/api";
import { sendAllTransactions } from "../../utils/transactions";
import { formatStringWithPlaceholders } from "../../utils/utils";

const SOCEAN_MINT = new PublicKey("5oVNBeEEQvYi1cX3ir8Dx5n1P7pdxydbGF2X4TxVusJm");

export const soceanUnstake = async (
  wallet: ConnectedWallet,
  connection: Connection,
  amount: number,
  onTransactionComplete: Dispatch<SetStateAction<boolean>>,
  notificationStrings: MultiTxnNotificationStrings,
) => {
  const socean = new Socean("mainnet-beta", connection);
  const amountToUnstake = new Numberu64(toLamports(amount, 9));

  const { transactionSequence: transactionSeq } = await socean.withdrawStakeTransactions(
    wallet.publicKey,
    amountToUnstake,
  );

  const transactions: Transaction[] = [];
  const signers: Signer[][] = [];

  for (const tseq of transactionSeq) {
    for (const t of tseq) {
      transactions.push(t.tx);
      signers.push(t.signers);
    }
  }

  return sendAllTransactions(
    connection,
    wallet,
    transactions,
    signers,
    {
      txnPendingMessage: formatStringWithPlaceholders(
        notificationStrings.youAreDoingAction.message,
        notificationStrings.youAreDoingAction.unstaked,
        "scSOL",
      ),
      txnSuccessMessage: formatStringWithPlaceholders(
        notificationStrings.youDidAction.message,
        notificationStrings.youDidAction.unstaked,
        "scSOL",
      ),
      onAllTransactionsConfirmed: () => onTransactionComplete(true),
      onSomeTransactionsConfirmed: () => onTransactionComplete(true),
    },
    confirmOptions,
    notificationStrings,
  );
};

export const soceanUnstakeInstant = async (
  wallet: ConnectedWallet,
  connection: Connection,
  amount: number,
  outAmount: number,
  onTransactionComplete: Dispatch<SetStateAction<boolean>>,
  multiTxnNotificationStrings: MultiTxnNotificationStrings,
  transactionVersion: TransactionVersion,
) => {
  const routes = await JupiterSwapHandler.getRoutes(
    SOCEAN_MINT,
    WRAPPED_SOL_MINT,
    toLamports(amount, 9),
    0.001,
    10,
    transactionVersion,
  );
  if (routes.length === 0) {
    throw new Error("No routes available");
  }
  const priceImpact = routes[0].priceImpactPct * 100;
  // reject if price impact >= 5%
  if (priceImpact >= 5) {
    throw new Error("Price impact too high");
  }

  const route = routes[0];
  const jupiterSwapHandler = new JupiterSwapHandler({
    connection,
    wallet,
    route,
    notificationStrings: multiTxnNotificationStrings,
    inputToken: {
      mint: SOCEAN_MINT,
      amount: amount,
      symbol: "scSOL",
    },
    outputToken: {
      mint: WRAPPED_SOL_MINT,
      amount: outAmount,
      symbol: "SOL",
    },
    transactionVersion: transactionVersion,
  });
  await jupiterSwapHandler.executeLegacyTransaction();
  onTransactionComplete(true);
};
